/* Joii — Step-by-step slider. No dependencies. */
(() => {
  if (customElements.get('joii-studies-steps')) return;

  const MOBILE_Q = '(max-width: 989px)';
  const DEPTH = 85;       // px pushed back per step (desktop coverflow)
  const STRETCH = 0.41;   // horizontal offset per step, as a fraction of card width
  const GAP = 16;         // px between cards on mobile
  const DOT_INSET = 8;    // handle centres on the first / last dot (dot radius)
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));

  class JoiiStepsSlider extends HTMLElement {
    connectedCallback() {
      this.stage = this.querySelector('[data-stage]');
      this.cards = [...this.querySelectorAll('[data-card]')];
      this.texts = [...this.querySelectorAll('[data-text]')];
      this.dots = [...this.querySelectorAll('[data-dot]')];
      this.track = this.querySelector('[data-track]');
      this.handle = this.querySelector('[data-handle]');
      this.prevBtn = this.querySelector('[data-prev]');
      this.nextBtn = this.querySelector('[data-next]');
      this.count = this.cards.length;
      this.progress = 0;
      this.index = 0;
      this.mq = window.matchMedia(MOBILE_Q);

      this.onResize = () => { this.measure(); this.render(); };
      this.onKey = this.onDocKey.bind(this);
      this.onBlockSelect = (e) => {
        if (!this.contains(e.target)) return;
        const i = this.cards.indexOf(e.target.closest('[data-card]'));
        if (i > -1) this.goTo(i);
      };

      this.prevBtn?.addEventListener('click', () => this.goTo(this.index - 1));
      this.nextBtn?.addEventListener('click', () => this.goTo(this.index + 1));
      this.dots.forEach((dot) => dot.addEventListener('click', () => this.goTo(+dot.dataset.dot)));
      this.bindStageDrag();
      this.bindHandleDrag();
      this.bindWheel();

      this.ro = new ResizeObserver(this.onResize);
      this.ro.observe(this);
      this.mq.addEventListener('change', this.onResize);
      document.addEventListener('keydown', this.onKey);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.measure();
      this.goTo(0, false);
    }

    disconnectedCallback() {
      this.ro?.disconnect();
      this.mq.removeEventListener('change', this.onResize);
      document.removeEventListener('keydown', this.onKey);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
    }

    get isMobile() { return this.mq.matches; }

    // horizontal px that moves the stack by one step
    get stepPx() { return this.isMobile ? this.cardW + GAP : this.cardW * STRETCH; }

    measure() {
      this.cardW = this.stage.offsetWidth || 1;
      if (this.track) {
        this.handleW = this.handle.offsetWidth;
        this.travel = Math.max(0, this.track.clientWidth - DOT_INSET * 2);
      }
    }

    setDragging(on) { this.classList.toggle('is-dragging', on); }

    goTo(i, animate = true) {
      this.index = clamp(Math.round(i), 0, this.count - 1);
      this.progress = this.index;
      this.setDragging(!animate);
      this.render();
      if (!animate) requestAnimationFrame(() => this.setDragging(false));
    }

    render() {
      const p = this.progress;
      const mobile = this.isMobile;
      const nearest = clamp(Math.round(p), 0, this.count - 1);

      this.cards.forEach((card, i) => {
        const d = i - p;
        const ad = Math.abs(d);
        const x = d * this.stepPx;
        const z = mobile ? 0 : -DEPTH * ad;
        card.style.transform = `translate3d(${x}px, 0, ${z}px)`;
        card.style.zIndex = String(this.count * 10 - Math.round(ad * 10));
        card.style.setProperty('--jss-shade-r', mobile ? 0 : clamp(d, 0, 1));
        card.style.setProperty('--jss-shade-l', mobile ? 0 : clamp(-d, 0, 1));
        card.setAttribute('aria-hidden', String(i !== nearest));
      });

      this.texts.forEach((text, i) => {
        text.style.opacity = String(clamp(1 - Math.abs(i - p), 0, 1));
        text.classList.toggle('is-active', i === nearest);
        text.setAttribute('aria-hidden', String(i !== nearest));
      });

      if (this.handle) {
        const ratio = this.count > 1 ? p / (this.count - 1) : 0;
        const x = DOT_INSET + clamp(ratio, 0, 1) * this.travel - this.handleW / 2;
        this.handle.style.transform = `translateX(${x}px)`;
        this.handle.setAttribute('aria-valuenow', String(nearest + 1));
        this.handle.setAttribute('aria-valuetext', `Step ${nearest + 1} of ${this.count}`);
      }
      this.dots.forEach((dot, i) => dot.toggleAttribute('aria-current', i === nearest));
      if (this.prevBtn) this.prevBtn.disabled = p <= 0.001;
      if (this.nextBtn) this.nextBtn.disabled = p >= this.count - 1.001;
    }

    // drag / swipe the card stack; a tap on a background card jumps to it
    bindStageDrag() {
      let startX = 0, startP = 0, dx = 0, active = false, moved = false;

      this.stage.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        active = true; moved = false; dx = 0;
        startX = e.clientX; startP = this.progress;
        this.stage.setPointerCapture(e.pointerId);
      });

      this.stage.addEventListener('pointermove', (e) => {
        if (!active) return;
        dx = e.clientX - startX;
        if (!moved && Math.abs(dx) < 6) return;
        moved = true;
        this.setDragging(true);
        // rubber-band past the first / last step
        let p = startP - dx / this.stepPx;
        if (p < 0) p /= 3; else if (p > this.count - 1) p = this.count - 1 + (p - this.count + 1) / 3;
        this.progress = p;
        this.render();
      });

      const end = (e) => {
        if (!active) return;
        active = false;
        if (!moved) {
          const card = document.elementsFromPoint(e.clientX, e.clientY)
            .find((el) => el.matches?.('[data-card]') && this.stage.contains(el));
          if (card) this.goTo(this.cards.indexOf(card));
          return;
        }
        let target = Math.round(this.progress);
        if (target === Math.round(startP) && Math.abs(dx) > 30) target -= Math.sign(dx);
        this.goTo(target);
      };
      this.stage.addEventListener('pointerup', end);
      this.stage.addEventListener('pointercancel', end);
    }

    // drag the avatar along the gradient track
    bindHandleDrag() {
      if (!this.handle) return;
      let grab = 0, active = false;

      this.handle.addEventListener('pointerdown', (e) => {
        if (e.button !== 0) return;
        active = true;
        const r = this.handle.getBoundingClientRect();
        grab = e.clientX - (r.left + r.width / 2);
        this.handle.setPointerCapture(e.pointerId);
        this.setDragging(true);
      });
      this.handle.addEventListener('pointermove', (e) => {
        if (!active || !this.travel) return;
        const x = e.clientX - grab - this.track.getBoundingClientRect().left - DOT_INSET;
        this.progress = clamp(x / this.travel, 0, 1) * (this.count - 1);
        this.render();
      });
      const end = () => {
        if (!active) return;
        active = false;
        this.goTo(this.progress);
      };
      this.handle.addEventListener('pointerup', end);
      this.handle.addEventListener('pointercancel', end);

      this.handle.addEventListener('keydown', (e) => {
        const map = { ArrowLeft: -1, ArrowDown: -1, ArrowRight: 1, ArrowUp: 1 };
        if (e.key in map) this.goTo(this.index + map[e.key]);
        else if (e.key === 'Home') this.goTo(0);
        else if (e.key === 'End') this.goTo(this.count - 1);
        else return;
        e.preventDefault();
        e.stopPropagation();
      });
    }

    // horizontal trackpad swipes over the cards; vertical scrolling is left alone
    bindWheel() {
      let acc = 0, locked = false, idle;
      this.stage.addEventListener('wheel', (e) => {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
        e.preventDefault();
        clearTimeout(idle);
        idle = setTimeout(() => { locked = false; acc = 0; }, 200);
        if (locked) return;
        acc += e.deltaX;
        if (Math.abs(acc) > 40) {
          locked = true;
          this.goTo(this.index + Math.sign(acc));
        }
      }, { passive: false });
    }

    // left / right arrow keys while the slider is on screen
    onDocKey(e) {
      if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
      if (e.defaultPrevented || e.altKey || e.ctrlKey || e.metaKey) return;
      const t = e.target;
      if (t.closest?.('input, textarea, select, [contenteditable], [role="slider"]')) return;
      const r = this.getBoundingClientRect();
      if (r.bottom <= 0 || r.top >= window.innerHeight) return;
      this.goTo(this.index + (e.key === 'ArrowRight' ? 1 : -1));
    }
  }

  customElements.define('joii-studies-steps', JoiiStepsSlider);
})();
